import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const Home = () => {
  // Client 2026-08-08: added chargesTtc / surfaceM2 / periods, dropped `reports`.
  // The "Reports Generated" tile counted rows from reports/keys — i.e. keys, not
  // reports — so it showed the same number as "Active Keys" beside it. The client
  // asked for it to go rather than be relabelled.
  const [stats, setStats] = useState({
    buildings: 0,
    activeKeys: 0,
    periods: 0,
    chargesTtc: 0,
    surfaceM2: 0,
    teamMembers: 0,
  });
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.access_level === 1;

  useEffect(() => {
    const fetchStats = async () => {
      const headers = {
        "api-key": process.env.REACT_APP_API_KEY,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      const requests = [
        // Only ACTIVE properties count, exactly like the three aggregate tiles below.
        // Without the filter this tile would keep counting Inactive and Training
        // buildings while periods/charges/surface excluded them — the same number
        // the client asked to have them removed from (2026-09-11). pageSize is
        // irrelevant here since only pagination.total is read, but it is sent so a
        // future reader does not assume this call returns every building.
        axios.get(`${process.env.REACT_APP_API_URL}building/list`, {
          headers,
          params: { page: 1, pageSize: 1, status: "Active" },
        }),
        isAdmin
          ? axios.get(`${process.env.REACT_APP_API_URL}key/list`, { headers })
          : Promise.resolve(null),
        // Replaces the reports/keys call that fed the removed "Reports Generated"
        // tile. One aggregate query for all three new figures.
        axios.get(`${process.env.REACT_APP_API_URL}building/dashboard-stats`, { headers }),
        isAdmin
          ? axios.get(`${process.env.REACT_APP_API_URL}auth/list_users`, { headers })
          : Promise.resolve(null),
      ];

      const [buildingsRes, keysRes, aggRes, teamRes] = await Promise.allSettled(requests);
      const agg = aggRes.status === "fulfilled" ? aggRes.value?.data?.data || {} : {};

      setStats({
        buildings:
          buildingsRes.status === "fulfilled"
            ? buildingsRes.value?.data?.data?.pagination?.total || 0
            : 0,
        activeKeys:
          keysRes.status === "fulfilled" && keysRes.value
            ? keysRes.value?.data?.data?.pagination?.total || 0
            : 0,
        periods: Number(agg.total_periods) || 0,
        chargesTtc: Number(agg.total_charges_ttc) || 0,
        surfaceM2: Number(agg.total_surface_m2) || 0,
        teamMembers:
          teamRes.status === "fulfilled" && teamRes.value
            ? teamRes.value?.data?.data?.pagination?.total || 0
            : 0,
      });

      setLoading(false);
    };

    fetchStats();
  }, [isAdmin]);

  const formatStat = (num) => String(num).padStart(2, "0");

  // Swiss formatting, matching the reports ("1'234.56"). The tiles round to whole
  // units: a KPI tile is not the place for rappen, and "9'403'062.24" is wide enough
  // to wrap the card at narrow widths. The exact value is still available on hover
  // via `title`, and the report itself is the authoritative figure.
  const chf = (num, decimals = 0) =>
    Number(num || 0).toLocaleString("de-CH", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  const statCards = [
    {
      label: "Total Buildings",
      value: formatStat(stats.buildings),
      icon: "icon-icon-20",
    },
    {
      label: "Active Keys",
      value: formatStat(stats.activeKeys),
      icon: "icon-icon-21",
    },
    // The three tiles the client asked for. Charges and surface are taken from each
    // building's LAST period and summed across buildings — not summed over every
    // period, which would count a multi-year building several times.
    {
      label: "Total Periods",
      value: formatStat(stats.periods),
      icon: "icon-icon-24",
    },
    // Icons follow the sidebar's own mapping (see SideBar.js) so a glyph means the
    // same thing everywhere: 20 = Buildings, 21 = Keys, 23 = Calculations,
    // 24 = Reports, 29 = Imported Data, 19 = My Team.
    // Charges uses the Calculations glyph rather than 22, which is Import Data — an
    // import arrow on a money total read as "import" at a glance. Surface uses 29
    // rather than 20, which Total Buildings already owns; two tiles with an
    // identical icon is the kind of detail that makes a dashboard look unfinished.
    {
      label: "Total Charges TTC",
      value: chf(stats.chargesTtc),
      exact: `CHF ${chf(stats.chargesTtc, 2)}`,
      icon: "icon-icon-23",
    },
    {
      label: "Total Surface",
      value: `${chf(stats.surfaceM2)} m²`,
      exact: `${chf(stats.surfaceM2, 2)} m²`,
      icon: "icon-icon-29",
    },
    {
      label: "Team Members",
      value: formatStat(stats.teamMembers),
      icon: "icon-icon-19",
    },
  ];

  const quickActions = [
    {
      label: "Add Building",
      description: "Create a new property entry",
      icon: "icon-icon-20",
      link: "/buildings/create-building",
    },
    ...(isAdmin
      ? [
          {
            label: "Create Key",
            description: "Define calculation parameters",
            icon: "icon-icon-21",
            link: "/create-key",
          },
        ]
      : []),
    {
      label: "Import Data",
      description: "Upload Excel/CSV files",
      icon: "icon-icon-22",
      link: "/import-data",
    },
  ];

  return (
    <div className="admin-page dashboard-page">
      <div className="dashboard-header">
        <h1 className="page-section-title">Dashboard</h1>
        <p className="dashboard-subtitle">
          Welcome back, {user?.name || "Admin User"}. Here's what's happening with your properties today.
        </p>
      </div>

      {/* col-xl-4 rather than col-xl-3: there are six tiles now, which lays out as
          two rows of three instead of a row of four and an orphaned row of two. */}
      <div className="row g-4 mb-5">
        {statCards.map((card) => (
          <div key={card.label} className="col-12 col-sm-6 col-xl-4">
            <div className="stat-card">
              <div className="stat-card-body">
                <p className="stat-label">{card.label}</p>
                {/* Each card supplies its own formatted string — the counters are
                    zero-padded, charges and surface are Swiss-formatted — so this
                    renders card.value as-is rather than re-formatting it here. */}
                <h2 className="stat-value" title={loading ? undefined : card.exact}>
                  {loading ? "--" : card.value}
                </h2>
              </div>
              <div className="stat-icon-circle">
                <i className={card.icon}></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="quick-actions-section">
        <h2 className="quick-actions-title">Quick Actions</h2>
        <div className="row g-4">
          {quickActions.map((action, index) => (
            <div key={index} className="col-12 col-md-4">
              <Link to={action.link} className="quick-action-card">
                <div className="quick-action-icon">
                  <i className={action.icon}></i>
                </div>
                <h3 className="quick-action-label">{action.label}</h3>
                <p className="quick-action-desc">{action.description}</p>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
